import React, { ComponentType, useEffect, useMemo, useRef, useState } from 'react';
import styles from './index.module.less';
import { useTranslation } from 'next-i18next';
import { Button, message } from 'antd';
import Image from 'next/image';
import { useWindowSize } from 'react-use';
import { HeroData, SearchLimitLeftModel } from '@/types/home';
import { useRouter } from 'next/router';
import Link from 'next/link';
import { cn } from '@/lib/utils';
import Disclaimer from '../Disclaimer';
import FreeTimes from '../FreeTimes';
import { IconHighlights, IconPosts, IconStory, IconVideo } from '@/components/LandingPage/TopSearch/switch-icon';
import TurnstileModal, { TurnstileModalHandle } from '@/components/TurnstileModal';

interface TopSearchContentProps {
  hideSearch?: boolean;
  inputClassName?: string;
  inputIcon?: React.ReactNode;
  inputPlaceholder?: string;
  showSearchBtnWithFullWidth?: boolean;
  btnPlaceholder?: string;
  displayTagsAndMiddleContentBelowSearchBox?: boolean;
  topInfo?: HeroData;
  middleContent?: React.ReactNode;
  loading?: boolean;
  leftModel?: SearchLimitLeftModel;
  /** 访客的使用情况，未登录试用 */
  guestUsage?: SearchLimitLeftModel;
  searchTabs?: {
    label: string | React.ReactNode;
    value: string;
  }[];
  defaultSearchTab?: string;
  onSearchTabClick?: (value: string) => void;
  searchVal?: string;
  onSearchClick?: (searchStr?: string) => void;
  onValueChanged?: (e?: string) => void;
  onInputVisibleChanged?: (visible?: boolean) => void;
  onUnlimitedClick?: () => void;
  onLoginClick?: () => void;
  errorCode?: string | number;
  resetErrorCode?: () => void;
  /** 是否启用人机校验 */
  needTurnstile?: boolean;
  /** 人机校验通过后携带 token 触发搜索 */
  onSearchWithTurnstile?: (searchStr: string, turnstileToken: string) => void;
}

interface ITab {
  path: string;
  title: string;
  Icon: ComponentType<{ className?: string; size?: number; color?: string }>;
}

const TopSearchContent = ({
  hideSearch = false,
  inputClassName,
  inputIcon,
  inputPlaceholder,
  showSearchBtnWithFullWidth,
  btnPlaceholder,
  displayTagsAndMiddleContentBelowSearchBox,
  topInfo,
  middleContent,
  loading,
  leftModel,
  guestUsage,
  searchVal,
  onSearchClick,
  searchTabs,
  defaultSearchTab,
  onSearchTabClick,
  onValueChanged,
  onInputVisibleChanged,
  onUnlimitedClick,
  onLoginClick,
  errorCode,
  resetErrorCode,
  needTurnstile = false,
  onSearchWithTurnstile,
}: TopSearchContentProps) => {
  const { t } = useTranslation();
  const [searchStr, setSearchStr] = useState(searchVal);
  const inputRef = useRef(null);
  const isComposingRef = useRef(false);
  const turnstileRef = useRef<TurnstileModalHandle>(null);
  const { width } = useWindowSize();
  const router = useRouter();
  const pathname = router.asPath.replace(/[?#].*$/, '');

  useEffect(() => {
    const handleScroll = () => {
      if (inputRef.current) {
        const rect = inputRef.current.getBoundingClientRect();
        onInputVisibleChanged && onInputVisibleChanged(rect.top >= 0 && rect.bottom <= window.innerHeight);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    if (searchVal !== searchStr) {
      setSearchStr(searchVal);
    }
  }, [searchVal]);

  useEffect(() => {
    if (errorCode) {
      setSearchStr('');
    }
  }, [errorCode]);

  const list = useMemo(() => {
    const tempList = topInfo?.tags;
    return width > 768 ? tempList : tempList?.slice(0, Math.min(2, tempList.length));
  }, [width, topInfo?.tags]);

  const isShow = useMemo(() => {
    const arr = [
      '/download-from-instagram',
      '/download-from-instagram/story',
      '/download-from-instagram/highlights',
      '/download-from-instagram/post',
      '/download-from-instagram/video',
      '/instagram-viewer',
      '/instagram-viewer/posts',
      '/instagram-viewer/highlights',
      '/anonymous-instagram-story-viewer',
      '/web-viewer-for-instagram',
    ];
    return arr.includes(pathname);
  }, [pathname]);

  const withoutLogin = useMemo(() => {
    const arr = [
      '/download-from-instagram',
      '/download-from-instagram/story',
      '/download-from-instagram/highlights',
      '/download-from-instagram/post',
      '/download-from-instagram/video',
      '/instagram-viewer',
      '/web-viewer-for-instagram',
      '/anonymous-instagram-story-viewer',
      '/instagram-viewer/highlights',
    ];
    return arr.includes(pathname);
  }, [pathname]);

  const isShowTabSwitch = useMemo(() => {
    const arr = [
      '/download-from-instagram',
      '/download-from-instagram/story',
      '/download-from-instagram/highlights',
      '/download-from-instagram/post',
      '/download-from-instagram/video',
    ];
    return arr.includes(pathname);
  }, [pathname]);

  const isShowSampleReport = useMemo(() => {
    const arr = [
      '/',
      '/view-instagram-anonymously',
      '/view-instagram-anonymously/users-search',
      '/view-instagram-anonymously/comments',
      '/instagram-tracker',
      '/instagram-tracker/follower-tracker',
      '/instagram-tracker/activity-tracker',
      '/instagram-tracker/see-someones-likes',
      '/instagram-influencer-trends',
      '/instagram-influencer-trends/track-activity',
      '/instagram-influencer-trends/uncover-gossip',
      '/catch-instagram-cheaters',
      '/catch-instagram-cheaters/track-cheating-activity',
    ];
    return arr.includes(pathname);
  }, [pathname]);

  const switchBar = useMemo(() => {
    if (!isShowTabSwitch) {
      return null;
    }

    const tabs: ITab[] = [
      {
        path: '/download-from-instagram/video',
        title: 'Video',
        Icon: IconVideo,
      },
      {
        path: '/download-from-instagram/story',
        title: 'Story',
        Icon: IconStory,
      },
      {
        path: '/download-from-instagram/highlights',
        title: 'Highlights',
        Icon: IconHighlights,
      },
      {
        path: '/download-from-instagram/post',
        title: 'Posts',
        Icon: IconPosts,
      },
    ];

    return (
      <div className="max-lg:mt-14 flex bg-[#ECEDF4] lg:w-fit mx-auto lg:border border-2 border-x-0 border-[#D9D9D9] lg:rounded-md overflow-hidden fixed w-[100vw] overflow-x-auto scrollbar-hide top-2 left-0 right-0 z-50 lg:relative">
        {tabs.map((item) => (
          <Link
            key={item.path}
            className={cn(
              'py-2 flex items-center text-nowrap justify-center text-base h-10 group hover:text-[#725CFF]',
              {
                'text-[#725CFF] bg-white cursor-default': pathname === item.path,
              }
            )}
            href={item.path}
            onClick={(e) => {
              if (pathname === item.path) {
                e.preventDefault();
              }
            }}
          >
            <span
              className={cn('flex items-center border-r px-5', {
                'border-r-transparent': pathname === item.path,
              })}
            >
              <item.Icon className="w-5 h-5 mr-1.5" />
              {item.title}
            </span>
          </Link>
        ))}
      </div>
    );
  }, [isShowTabSwitch, pathname]);

  const renderBasicContent = () => {
    const Title = topInfo?.seoTitle ? 'p' : 'h1';

    return (
      <>
        {topInfo?.title && (
          <Title className={styles.title} style={topInfo.titleStyle}>
            {topInfo?.title}
          </Title>
        )}

        {topInfo?.seoTitle && (
          <h1 className={styles.title} style={{ display: 'none' }}>
            {topInfo?.seoTitle}
          </h1>
        )}

        {topInfo?.subTitle && (
          <p className={styles.subTitle} style={topInfo.subTitleStyle}>
            {topInfo?.subTitle}
          </p>
        )}

        {topInfo?.desc && (
          <p className={styles.subDesc} style={topInfo.descStyle}>
            {topInfo?.desc}
          </p>
        )}
      </>
    );
  };

  const renderTags = () => {
    return (
      list?.length > 0 && (
        <div
          className={styles.list}
          style={{
            gridTemplateColumns: `repeat(${list?.length >= 4 ? 4 : list?.length}, 1fr)`,
            ...topInfo.tagsStyle,
          }}
        >
          {list.map((item, index) => {
            return (
              <div key={index} className={styles.listItem}>
                <Image src={'/images/home/home-select-icon.webp'} alt="" width={17} height={12} />
                <div dangerouslySetInnerHTML={{ __html: item }} />
              </div>
            );
          })}
        </div>
      )
    );
  };

  const renderSearchTabs = () => {
    return (
      <div className="h-12 mt-10 flex items-center cursor-pointer overflow-hidden rounded-t-xl">
        {searchTabs?.map((item) => (
          <div
            key={item.value}
            className={cn('flex-1 flex justify-center items-center h-full hover:bg-[#ECEDF4] transition-colors', {
              'text-[#725CFF] bg-[#ECEDF4]': item.value === defaultSearchTab,
            })}
            onClick={() => {
              onSearchTabClick?.(item.value);
            }}
          >
            {item.label}
          </div>
        ))}
      </div>
    );
  };

  const renderSearch = () => {
    const handleSearch = async (value?: string) => {
      const searchValue = value?.trim();
      if (!searchValue) {
        message.error(t('Please enter the insert link or username'));
        return;
      }
      if (needTurnstile) {
        const result = await turnstileRef.current?.open();
        if (!result?.success) {
          return;
        }
        if (result.token && onSearchWithTurnstile) {
          onSearchWithTurnstile(searchValue, result.token);
          return;
        }
      }
      onSearchClick?.(searchValue);
    };

    return (
      <div
        className={cn({
          'p-4 bg-[#ECEDF4] rounded-b-xl': !!searchTabs,
        })}
      >
        <div
          className={cn(
            styles.inputContainer,
            'w-[520px] pl-4 py-2 pr-2 bg-white',
            {
              'mt-10': !searchTabs,
              'w-full': !!searchTabs,
            },
            inputClassName
          )}
          ref={inputRef}
        >
          {inputIcon ? (
            inputIcon
          ) : (
            <Image src={'/images/home/instagram-icon.webp'} alt="instagram" width={24} height={24} />
          )}
          <input
            value={searchStr}
            onChange={(e) => {
              setSearchStr(e.target.value);
              onValueChanged && onValueChanged(e?.target?.value);
            }}
            onKeyDown={(e) => {
              if (e?.key === 'Enter' && !isComposingRef.current && !e.nativeEvent.isComposing) {
                const value = (e.target as HTMLInputElement).value;
                void handleSearch(value);
              }
            }}
            onCompositionStart={() => {
              isComposingRef.current = true;
            }}
            onCompositionEnd={(e) => {
              isComposingRef.current = false;
              setSearchStr(e.currentTarget.value);
              onValueChanged && onValueChanged(e.currentTarget.value);
            }}
            onFocus={() => {
              if (errorCode) {
                resetErrorCode && resetErrorCode();
              }
            }}
            placeholder={inputPlaceholder ?? t('Enter profile link or username')}
          />
          <Button
            loading={loading}
            className={cn(styles.searchBtn, 'min-w-[145px] max-md:min-w-[100px]', {
              'max-md:hidden': showSearchBtnWithFullWidth,
            })}
            onClick={() => {
              void handleSearch(searchStr);
            }}
          >
            {btnPlaceholder ?? t('Search Now')}
          </Button>
        </div>
        <Button
          loading={loading}
          className={cn(styles.searchBtnWithFullWidth, {
            hidden: true,
            'max-md:block': showSearchBtnWithFullWidth,
          })}
          onClick={() => {
            void handleSearch(searchStr);
          }}
        >
          {btnPlaceholder ?? t('Search Now')}
        </Button>
        {!!errorCode && (
          <div className={styles.error}>
            <span>{t('ThisInstagramAccountIsPrivate_Desc')}</span>
          </div>
        )}
      </div>
    );
  };

  if (!topInfo) {
    return null;
  }

  return (
    <div className={styles.content}>
      {/* 基础内容 */}
      {switchBar}

      {renderBasicContent()}

      {/* 标签和自定义的中间内容，搜索框之前显示 */}
      {!displayTagsAndMiddleContentBelowSearchBox && (
        <>
          {renderTags()}
          {middleContent}
        </>
      )}

      {/* 搜索选项卡 */}
      {searchTabs && renderSearchTabs()}

      {/* 搜索框 */}
      {!hideSearch && renderSearch()}

      {/* 标签和自定义的中间内容，搜索框之后显示 */}
      {displayTagsAndMiddleContentBelowSearchBox && (
        <>
          {renderTags()}
          {middleContent}
        </>
      )}

      {/* 示例报告入口 */}
      {isShowSampleReport && (
        <div className="mt-8">
          {t('See what our reports offer')} -{' '}
          <Link className="text-[#725CFF]" href="/instagram-tracking-example">
            {t('Sample here')}
          </Link>
        </div>
      )}

      {/* 免费次数 */}
      {!withoutLogin && (
        <FreeTimes
          leftModel={leftModel}
          onUnlimitedClick={onUnlimitedClick}
          guestUsage={guestUsage}
          onLoginClick={onLoginClick}
        />
      )}
      {/* 免责声明 */}
      <Disclaimer className={`${!isShow ? 'hidden' : ''}`} />
      {needTurnstile && <TurnstileModal ref={turnstileRef} />}
    </div>
  );
};

export default TopSearchContent;
